import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ScanFace, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { DisclaimerModal } from "../components/DisclaimerModal";
import { EmailAuthForm } from "../components/EmailAuthForm";
import { AppleSignInButton } from "../components/AppleSignInButton";
import { AppleSignInWebButton } from "../components/AppleSignInWebButton";
import { PromoScarcityBanner } from "../components/PromoScarcityBanner";
import { PromoCodeField } from "../components/PromoCodeField";
import { OpenInAppCTA } from "../components/OpenInAppCTA";
import { showGoogleSignIn } from "../lib/platform";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";

// Unified Sign-In landing for the marketing surfaces.
// Returning users → Google sign-in (optionally Face ID where the device
// supports it). New users → pick a plan, which routes them through the
// /pricing page where the trial-vs-pay-now choice is made and Stripe is
// triggered after sign-up.
const SignInPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [searchParams] = useSearchParams();
  const authErrorCode = searchParams.get("auth_error");
  const isStaleSession = authErrorCode === "stale_session";
  const isNetworkAuthError = authErrorCode === "network";
  const isUnknownAuthError = authErrorCode === "unknown";
  const showAuthErrorBanner = isStaleSession || isNetworkAuthError || isUnknownAuthError;
  useSeo({
    title: "Sign in — Bilateral Calm",
    description:
      "Sign in to Bilateral Calm with Google or email to continue your guided bilateral-stimulation practice.",
    path: "/sign-in",
  });

  // If the user is already signed in, send them straight to the dashboard.
  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const hasBiometric =
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined";

  return (
    <div
      className="min-h-screen bg-[#FBF8F1] flex flex-col"
      data-testid="sign-in-page"
    >
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center px-6 py-8">
      <div className="w-full max-w-3xl">
        {/* Tagline — same line used on Home + Learn More for brand consistency */}
        <p
          className="font-outfit text-lg sm:text-xl text-[#1B2349]/85 text-center max-w-xl mx-auto mb-10 leading-snug"
          data-testid="signin-tagline"
        >
          An easier, kinder way to{" "}
          <em className="italic font-semibold text-[#3090A8]">regulate</em>{" "}
          your nervous system.
        </p>

        {/* iPhone / iPad Safari steer — no-op on desktop, Android, and the
            App Store app. Renders above the auth banner so it doesn't stack
            awkwardly under an error state. */}
        <OpenInAppCTA />

        {/* Auth-recovery banner — shown when AuthCallback failed. The
            copy adapts to WHY it failed (see App.js AuthCallback catch):
              • network — user's connection dropped mid-exchange; retry
              • stale_session — Google returned an already-redeemed
                session_id; usually needs a Google-side clear
              • unknown — surface a soft "try again" message
            Keeps folks who hit a mobile WiFi hiccup from being lectured
            about clearing Google cookies. */}
        {showAuthErrorBanner && (
          <div
            className="mb-8 rounded-2xl border border-orange-200 bg-orange-50 p-5 sm:p-6"
            data-testid="signin-stale-session-banner"
          >
            <div className="flex gap-3 items-start">
              <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                {isNetworkAuthError && (
                  <>
                    <h3 className="font-outfit font-semibold text-[#1B2349] mb-1">
                      Connection dropped during sign-in
                    </h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">
                      Your network hiccupped while we were finishing sign-in.
                      Check your connection and try again below — usually a
                      second attempt just works.
                    </p>
                  </>
                )}
                {isUnknownAuthError && (
                  <>
                    <h3 className="font-outfit font-semibold text-[#1B2349] mb-1">
                      Something went wrong finishing sign-in
                    </h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">
                      Please try signing in again. If it happens twice in a
                      row, try the email + password option instead.
                    </p>
                  </>
                )}
                {isStaleSession && (
                  <>
                    <h3 className="font-outfit font-semibold text-[#1B2349] mb-1">
                      Your previous sign-in is still cached
                    </h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed mb-3">
                      Google has saved a stale sign-in session in this browser.
                      Pick one of these to continue:
                    </p>
                    <ul className="text-sm text-[#4A5568] leading-relaxed list-disc pl-5 space-y-1.5">
                      <li>
                        Open this site in a <strong>private / incognito window</strong>{" "}
                        (the fastest path).
                      </li>
                      <li>
                        Go to{" "}
                        <a
                          href="https://accounts.google.com/Logout"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#3090A8] underline underline-offset-2 font-medium"
                          data-testid="signin-google-logout-link"
                        >
                          accounts.google.com/Logout
                        </a>{" "}
                        in another tab, then come back here.
                      </li>
                      <li>
                        Use the <strong>email + password sign-up</strong> on the right
                        instead.
                      </li>
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Two-column choice card */}
        <div className="bg-white/85 backdrop-blur-sm rounded-3xl border border-slate-100 shadow-[0_14px_44px_rgba(0,0,0,0.06)] overflow-hidden grid md:grid-cols-2">
          {/* ── Returning users ── */}
          <div className="p-8 sm:p-10 flex flex-col" data-testid="signin-returning">
            <p className="text-xs uppercase tracking-[0.18em] text-[#3090A8] font-medium mb-2">
              Returning member
            </p>
            <h2 className="font-outfit font-semibold text-2xl text-[#1B2349] leading-tight mb-3">
              Welcome back.
            </h2>
            <p className="text-[#4A5568] leading-relaxed mb-6">
              Pick up where you left off. Your library, progress, and
              subscription stay synced.
            </p>

            <div className="mt-auto flex flex-col gap-2.5">
              {/* Apple Sign-In renders FIRST — on the iOS App Store build
                  it's the fastest one-tap flow. Returns null on non-iOS,
                  so web users don't see it. */}
              <AppleSignInButton redirectTo="/dashboard" />
              {/* Web Apple Sign-In — renders only on the web (bilateralcalm.com)
                  when REACT_APP_APPLE_WEB_SERVICES_ID is configured. Uses
                  Apple's JS SDK popup flow → POST /api/auth/apple. */}
              <AppleSignInWebButton
                label="Sign in with Apple"
                onSuccess={() => (window.location.href = "/dashboard")}
              />
              {showGoogleSignIn() && (
                <Button
                  onClick={login}
                  className="w-full justify-center rounded-full bg-[#1B2349] text-white font-medium py-3 hover:bg-[#0F1838]"
                  data-testid="signin-google-btn"
                >
                  Sign in with Google
                </Button>
              )}
              {hasBiometric && (
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  className="w-full justify-center rounded-full border-[#78C0D8] text-[#78C0D8] hover:bg-[#78C0D8] hover:text-white py-3"
                  data-testid="signin-faceid-btn"
                >
                  <ScanFace className="w-4 h-4 mr-2" />
                  Use Face ID
                </Button>
              )}
              <EmailAuthForm mode="signin" />
            </div>
          </div>

          {/* ── New users — DESKTOP full card. On mobile we render a
              compact link BELOW the whole card instead (see after the
              two-column grid). */}
          <div
            className="hidden md:flex p-8 sm:p-10 flex-col text-white"
            style={{ backgroundColor: "#1B2349" }}
            data-testid="signin-new"
          >
            <p className="text-xs uppercase tracking-[0.18em] text-[#A8D8D8] font-medium mb-2">
              New here?
            </p>
            <h2 className="font-outfit font-semibold text-2xl leading-tight mb-3">
              Start your practice today.
            </h2>
            <p className="text-white/85 leading-relaxed mb-6">
              Choose your plan, then start a free 5-day trial — or unlock the
              full library right away on the next page.
            </p>

            <div className="mt-auto flex flex-col gap-2.5">
              {/* Early-access scarcity + promo code entry for the sign-up
                  column. Hidden entirely when remaining > 15 and when the
                  program is full (banner switches to waitlist inside). */}
              <div className="text-left">
                <PromoScarcityBanner variant="compact" />
              </div>
              <div className="text-left">
                <PromoCodeField label="Have an early-access code?" />
              </div>
              <Button
                onClick={() => navigate("/pricing")}
                className="w-full justify-center rounded-full font-medium py-3 bg-white text-[#1B2349] hover:bg-[#F4F1E8]"
                data-testid="signin-choose-plan-btn"
              >
                See plans &amp; sign up
              </Button>
              <AppleSignInButton redirectTo="/pricing" />
              <AppleSignInWebButton
                label="Continue with Apple"
                onSuccess={() => (window.location.href = "/pricing")}
              />
              <EmailAuthForm mode="signup" compact />
              <button
                onClick={() => navigate("/login")}
                className="text-center text-sm font-medium text-white/85 hover:text-white py-1"
                data-testid="signin-learn-more-btn"
              >
                Learn more about Bilateral Calm
              </button>
            </div>
          </div>
        </div>

        {/* Mobile-only compact "New here?" strip. On mobile the full dark
            card above is hidden (md:flex on that block); this row keeps
            the sign-up path one tap away without competing visually with
            the sign-in form. */}
        <div
          className="md:hidden mt-4 text-center"
          data-testid="signin-mobile-new-link"
        >
          <p className="text-sm text-[#4A5568]">
            New here?{" "}
            <button
              onClick={() => navigate("/pricing")}
              className="text-[#3090A8] font-semibold underline underline-offset-2 hover:text-[#1B2349]"
              data-testid="signin-mobile-signup-link"
            >
              Start your 5-day free trial →
            </button>
          </p>
        </div>

        <p className="text-center text-base text-[#4A5568] mt-7">
          By continuing you agree to our{" "}
          <button
            onClick={() => setShowDisclaimer(true)}
            className="text-[#3090A8] font-medium underline underline-offset-2 hover:text-[#1B2349]"
            data-testid="signin-disclaimer-link"
          >
            disclaimer
          </button>
          {" "}and{" "}
          <button
            onClick={() => navigate("/privacy")}
            className="text-[#3090A8] font-medium underline underline-offset-2 hover:text-[#1B2349]"
            data-testid="signin-privacy-link"
          >
            privacy policy
          </button>
          .
        </p>
      </div>
      </div>

      <DisclaimerModal
        open={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
      />
      <SiteFooter />
    </div>
  );
};

export default SignInPage;
